import { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Box, Card, Collapse, IconButton, Typography } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CancelIcon from '@mui/icons-material/Cancel';
import QuestionForm from './QuestionForm';
import QuestionTypeIcon from './QuestionTypeIcon';
import { SurveyV2 } from '@act/shared/data-transfer-objects';
interface IDraggableQuestionCardProps {
  question: SurveyV2.CreateQuestionJSONDto & { id?: number };
  index: number;
  questionCount: number;
  isActionSurvey: boolean;
  handleMoveQuestion: (dragIndex: number, hoverIndex: number) => void;
  handleUpdateQuestion: (
    question: SurveyV2.CreateQuestionJSONDto & { id?: number }
  ) => void;
  handleRemoveQuestion: (questionIndex: number) => void;
  formErrors?: any; // Form validation errors from React Hook Form
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const DraggableQuestionCard = ({
  question,
  index,
  questionCount,
  isActionSurvey,
  handleMoveQuestion,
  handleUpdateQuestion,
  handleRemoveQuestion,
  formErrors,
}: IDraggableQuestionCardProps) => {
  const [open, setOpen] = useState<boolean>(true);

  const onToggle = () => setOpen(!open);

  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: string | symbol | null }
  >({
    accept: 'question',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      handleMoveQuestion(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'question',
    item: () => {
      return { id: question.id, index };
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0.4 : 1;
  drag(drop(ref));

  return (
    <Box
      ref={ref}
      data-handler-id={handlerId}
      sx={{
        opacity,
        cursor: 'move',
        width: '100%',
      }}
    >
      <Card
        sx={{
          width: '100%',
          padding: open ? '16px' : '16px 16px 0',
          boxSizing: 'border-box',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
          },
          transition: 'all 0.2s ease-in-out',
          border: '1px solid rgba(0,0,0,.35)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            mb: '16px',
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <QuestionTypeIcon type={question.type} />
            <Typography variant="h6" sx={{ color: 'text.primary' }}>
              {question.text ? question.text : `Question ${question.sortOrder}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={onToggle}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            <IconButton
              sx={{
                cursor: 'pointer',
                color: 'text.secondary',
                '&:hover': {
                  color: '#ff8a8a',
                },
                transition: 'color 0.2s ease-in-out',
              }}
              onClick={() => handleRemoveQuestion(question.sortOrder)}
            >
              <CancelIcon />
            </IconButton>
          </Box>
        </Box>
        <Collapse in={open} unmountOnExit>
          <QuestionForm
            question={question}
            questionCount={questionCount}
            isActionSurvey={isActionSurvey}
            onQuestionChange={handleUpdateQuestion}
            formErrors={formErrors}
            questionIndex={index}
          />
        </Collapse>
      </Card>
    </Box>
  );
};

export default DraggableQuestionCard;
